import { useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Spinner,
} from "reactstrap";
import { SubmitHandler, useForm } from "react-hook-form";

import InputGroup from "../common/input-group";
import { forgotPasswordV2, mapAuthError } from "@/services/authServiceV2";
import useAlertBar from "@/hooks/useAlertBar";

type Inputs = {
  email: string;
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email Address is required"),
});

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { openAlertBar } = useAlertBar();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema),
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit: SubmitHandler<Inputs> = async (formData) => {
    setLoading(true);
    try {
      // v2 always returns 204 (no account enumeration) and emails a reset LINK — there is
      // no code-verify step. Show a neutral confirmation regardless of whether the email exists.
      await forgotPasswordV2({ email: formData.email.toLowerCase() });
      setSent(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      const { message } = mapAuthError(
        error,
        "Unexpected error occurred. Please try again."
      );
      openAlertBar(message, false);
    }
  };

  return (
    <Container className="my-5">
      <Col sm={6} xs={12} className="mx-auto">
        <Card>
          <CardBody>
            {sent ? (
              <div className="text-center py-4">
                <h4 className="mb-3">Check your email</h4>
                <p className="text-muted mb-0">
                  If an account exists for that address, we&#39;ve sent a link
                  to reset your password. The link expires shortly — check your
                  spam folder if you don&#39;t see it.
                </p>
              </div>
            ) : (
              <Form
                onSubmit={handleSubmit(onSubmit)}
                className="my-4 needs-validation"
              >
                <h2 className="text-center mb-4">Forgot Password</h2>
                <InputGroup
                  required
                  label="Email Address"
                  name="email"
                  register={register}
                  error={errors.email?.message}
                />
                <Button type="submit" disabled={loading} className="w-100">
                  SEND RESET LINK
                  {loading && <Spinner size="sm" className="ms-3" />}
                </Button>
              </Form>
            )}
          </CardBody>
        </Card>
      </Col>
    </Container>
  );
}
